/** -- external -- */

import React, {
  useEffect,
  useState,
  useImperativeHandle,
  useMemo,
} from "react";
import _ from "lodash";
import classNames from "classnames";
import { connect } from "react-redux";
import { Alert } from "@toddle-edu/ds-web";

/** -- internal -- */

import { useI18n } from "Hooks";
import ToddleAnalytics from "AddOnManager/routes/OrgAddOn/components/ToddleAnalytics";
import ToddleAi from "AddOnManager/routes/OrgAddOn/components/ToddleAi";
import ManageMessaging from "AddOnManager/routes/OrgAddOn/components/ManageMessaging";
import IBISRevamp from "AddOnManager/routes/OrgAddOn/components/IBIS/IBISRevamp";
import AITutor from "AddOnManager/routes/OrgAddOn/components/AITutor";
import RolesAndPermissions from "AddOnManager/routes/OrgAddOn/components/RolesAndPermissions";
import UnitPlanningAI from "AddOnManager/routes/OrgAddOn/components/UnitPlanningAI";
import BluePrintSync from "AddOnManager/routes/OrgAddOn/components/BluePrintSync";
import WorkrampAcademy from "AddOnManager/routes/OrgAddOn/components/WorkrampAcademy";
import RosterSync from "AddOnManager/routes/OrgAddOn/components/RosterSync";
import TimetableSync from "AddOnManager/routes/OrgAddOn/components/TimetableSync";
import AttendanceSync from "AddOnManager/routes/OrgAddOn/components/AttendanceSync";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { PORTAL_TYPE_BY_REGION } from "Platform/routes/Regions/modules/Module";
import { setPortalType } from "Login/modules/LoginModule";

/** -- relative -- */

import ManageAccessForSnP from "./components/ManageAccessForSnP";
import ManageAccessForToddleLibrary from "./components/ManageAccessForToddleLibrary";
import {
  getAddOnOptions,
  getCurriculumPrograms,
  getPageInfo,
  getPreEnabledEntities,
  getOrgPlan,
  getCurriculumStatusesAfterPlanChange,
  getOrgAddonStatusAfterPlanChange,
  getAddonsToggleSummary,
} from "./modules/AddOnManagerUtils";
import classes from "./AddOnManager.scss";
import AddonsDatagrid from "./components/AddOnsDatagrid";
import {
  ADD_ON_TYPES,
  AI_TUTOR,
  ATTENDANCE_SYNC,
  AUTH_AND_EVAL,
  BLUEPRINT_SYNC,
  IBIS_REGISTRATION_REVAMP,
  LOADING_STATES,
  MESSAGING,
  ROLES_AND_PERMISSIONS,
  ROSTER_SYNC,
  TIMETABLE_SYNC,
  TODDLE_AI,
  TODDLE_ANALYTICS,
  TODDLE_LIBRARY,
  UNIT_PLANNING_AI,
  WORKRAMP_ACADEMY,
} from "./modules/AddOnManagerConstants";
import { toggleAddOnsSelection } from "./modules/AddOnManagerModules";
import { SOURCE } from "./components/AddOnsDatagrid/modules/AddonsDatagridUtils";
import AddonsConfirmationModal from "./components/AddonsConfirmationModal";
import { alertStyles } from "./styles";

/**
 * configurationState is an object with addOnId as key and value as:
 * {
 *    addedEntities: [array of entity ids which are added],
 *    removedEntities: [array of entity ids which are removed],
 *    isConfigurable: boolean,
 *    needsConfiguration: boolean
 * }
 * We will use this to track manual configuration changes made by user before saving
 *
 * addOnsStatus is an object with addOnId as key and value as:
 * {
 *    checked: boolean,
 *    curriculumStatus: object (if applicable),
 *    isConfigurable: boolean
 * }
 * This is to track the current status of add-ons as per backend
 */

const AddOnManager = ({
  isToddle20Flow = false,
  organizationDetails,
  isCreateFlow = false,
  customRef,
  toggleAddOnsSelection,
  modifiedSchoolPlanDetails,
  closeModal,
  setPortalType,
}) => {
  const [isLoading, setIsLoading] = useState(LOADING_STATES.FETCHING);
  const [addOnsStatus, setAddOnsStatus] = useState({});
  const [configurationState, setConfigurationState] = useState({});
  const [selectedAddOn, setSelectedAddOn] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const orgRegion = _.get(organizationDetails, "region", null);
  const orgId = _.get(organizationDetails, "id", null);
  const { organizationAddOns, curriculumAddOns, allAddOns } = getAddOnOptions();
  const { t } = useI18n(["common", "onBoarding"]);
  const testId = "addOnManager";

  // Org plan is determined by the highest plan among its curriculum programs
  const { curriculumPrograms, orgPlan } = useMemo(() => {
    const curriculumPrograms = getCurriculumPrograms({ organizationDetails });
    return { curriculumPrograms, orgPlan: getOrgPlan({ curriculumPrograms }) };
  }, [organizationDetails]);

  const { toggledOrgAddons, toggledCurriculumAddons } = useMemo(() => {
    return getAddonsToggleSummary({
      configurationState,
      addOnsStatus,
    });
  }, [addOnsStatus, configurationState]);

  useImperativeHandle(customRef, () => {
    return {
      isValid: () => {
        const invalidAddOns = _.filter(configurationState, (config, key) => {
          const needsConfiguration = _.get(config, "needsConfiguration", false);
          const addedEntities = _.get(config, "addedEntities", []);

          /**
           * AddOns with { needConfiguration: true } must be configured through the configuration modal;
           * they cannot be enabled directly from the datagrid.
           *
           * When the user completes the configuration, the entity’s ID is removed from addedEntities.
           *
           * Therefore, if addedEntities is empty, it means the user has configured all toggled entities
           * for that addOn. If it's not empty, some toggled entities are still unconfigured.
           */
          return needsConfiguration && !_.isEmpty(addedEntities);
        });
        return _.isEmpty(invalidAddOns);
      },
      onSave: async () => {
        if (!_.isEmpty(toggledOrgAddons) || !_.isEmpty(toggledCurriculumAddons))
          toggleConfirmationModal();
        else closeModal();
      },
    };
  });

  useEffect(() => {
    if (orgRegion) setPortalType(PORTAL_TYPE_BY_REGION[orgRegion]);

    return () => {
      setPortalType(null);
    };
  }, [orgRegion, setPortalType]);

  useEffect(() => {
    if (!orgId) return;

    const fetchAddOnsStatus = async () => {
      setIsLoading(LOADING_STATES.FETCHING);
      const addOnsStatusPromises = _.map(allAddOns, async addOn => {
        const {
          getAddOnStatusFn,
          addOnsParameters,
          id: addOnId,
          isConfigurable = true,
          addOnType,
          needsConfiguration,
          enabledWithPlans,
        } = addOn;
        try {
          const {
            checked = false,
            curriculumStatus,
            enabledModules = [],
          } = await getAddOnStatusFn({
            addOnsParameters,
            orgId,
            addOnId,
            isInitialFetch: true,
          });

          // If addon is organization level
          if (
            addOnType === ADD_ON_TYPES.ORGANIZATION_LEVEL &&
            !_.isEmpty(enabledWithPlans)
          ) {
            const orgAddOnStatus = getOrgAddonStatusAfterPlanChange({
              isChecked: checked,
              orgPlan,
              addOn,
              enabledModules,
              modifiedSchoolPlanDetails,
            });
            return orgAddOnStatus;
          }

          // if addon is curriculum level
          const finalCurriculumStatus = getCurriculumStatusesAfterPlanChange({
            entityStatuses: curriculumStatus,
            modifiedSchoolPlanDetails,
            addOn,
            isConfigurable,
            addOnType,
            needsConfiguration,
            orgPlan,
          });

          return {
            addOnId,
            checked,
            curriculumStatus: finalCurriculumStatus,
            isConfigurable,
            addOnType,
          };
        } catch (e) {
          return { addOnId, addOnType, checked: false, isConfigurable: true };
        }
      });

      const addOnsStatus = await Promise.all(addOnsStatusPromises);
      setIsLoading(null);
      const addOnsStatusMap = _.reduce(
        addOnsStatus,
        (
          acc,
          {
            addOnId,
            checked,
            curriculumStatus,
            isConfigurable,
            addOnType,
            isPromoted,
            isDemoted,
            source,
            enabledModules,
          }
        ) => {
          acc[addOnId] = {
            checked,
            isPromoted,
            isDemoted,
            source,
            ...(curriculumStatus && { curriculumStatus }),
            isConfigurable,
            addOnType,
            enabledModules,
          };
          return acc;
        },
        {}
      );
      setAddOnsStatus(addOnsStatusMap);
    };
    fetchAddOnsStatus();
  }, [orgId]);

  useEffect(() => {
    if (isLoading) return;

    const addOns = _.reduce(
      allAddOns,
      (acc, addOn) => {
        const addOnId = addOn?.id;
        if (!addOnId) return acc;

        const { addedEntities, removedEntities } = getPreEnabledEntities({
          addOn,
          addOnStatus: addOnsStatus?.[addOnId],
          isCreateFlow,
          orgPlan,
          curriculumPrograms,
          orgId,
          isToddle2Org: isToddle20Flow,
        });

        acc[addOnId] = {
          addOnType: addOn.addOnType ?? null,
          addedEntities,
          removedEntities,
          isConfigurable: addOn.isConfigurable,
          needsConfiguration: addOn.needsConfiguration,
        };
        return acc;
      },
      {}
    );
    setConfigurationState(addOns);
  }, [isLoading]);

  const pageInfo = getPageInfo({
    t,
    isToddle20Flow,
  });

  const toggleAddOn = ({ addOnId, entityId }) => {
    if (!addOnId || !entityId) return;
    setSelectedAddOn({
      addOn: _.find(allAddOns, { id: addOnId }),
      entityId,
    });
  };

  const closeAddOnModal = () => {
    setSelectedAddOn(null);
  };

  const removeEntityFromConfigState = ({ addOnId, entityId }) => {
    setConfigurationState(prevState => {
      const prevAddOnConfig = _.get(prevState, addOnId, {});
      const addedEntities = _.get(prevAddOnConfig, "addedEntities", []);
      const removedEntities = _.get(prevAddOnConfig, "removedEntities", []);

      return {
        ...prevState,
        [addOnId]: {
          ...prevAddOnConfig,
          addedEntities: _.without(addedEntities, entityId),
          removedEntities: _.without(removedEntities, entityId),
        },
      };
    });
  };

  const onSuccessAddOnConfigChange = async ({
    addOnId,
    curriculumProgramId,
  }) => {
    const addOn = _.find(allAddOns, { id: addOnId });
    if (!addOn) return;

    const { getAddOnStatusFn, addOnsParameters, isConfigurable = true } = addOn;
    try {
      const { checked = false, curriculumStatus } = await getAddOnStatusFn({
        addOnsParameters,
        orgId,
        addOnId,
      });

      setAddOnsStatus(prevState => {
        const existing = prevState?.[addOnId] || {};

        if (existing.addOnType === ADD_ON_TYPES.ORGANIZATION_LEVEL) {
          return {
            ...prevState,
            [addOnId]: {
              ...existing,
              checked,
              isConfigurable,
              source: SOURCE.QUERY,
            },
          };
        }

        const oldArray = existing.curriculumStatus || [];

        const updatedArray = oldArray.map(item =>
          item.id === curriculumProgramId
            ? {
                ...item,
                ...(curriculumStatus?.find(
                  cs => cs.id === curriculumProgramId
                ) || {}),
                source: SOURCE.QUERY,
              }
            : item
        );

        return {
          ...prevState,
          [addOnId]: {
            ...existing,
            checked,
            isConfigurable,
            ...(updatedArray && { curriculumStatus: updatedArray }),
          },
        };
      });

      // RESET MANUAL CONFIGURATION CHANGES AFTER SUCCESSFUL UPDATE
      removeEntityFromConfigState({
        addOnId,
        entityId: _.get(selectedAddOn, "entityId"),
      });
    } catch (e) {
      console.error(`Failed to update status for ${addOnId}:`, e);
    }
  };

  const onToggleCheckbox = ({ addOnId, entityId, isChecked }) => {
    setConfigurationState(prevState => {
      const prevConfig = _.get(prevState, addOnId, {});
      const added = _.get(prevConfig, "addedEntities", []);
      const removed = _.get(prevConfig, "removedEntities", []);

      const addOnDetails = addOnsStatus?.[addOnId];
      const addOnType = _.get(addOnDetails, "addOnType", null);
      const checkedSource = _.get(addOnDetails, "source", null);
      const isOrgAddonCheckedByDefault = _.get(addOnDetails, "checked", false);

      // This is to handle the scenario where user tries to uncheck an add-on which is auto-enabled due to plan change.
      let skipRemovedUpdate = false;

      if (
        addOnType === ADD_ON_TYPES.ORGANIZATION_LEVEL &&
        checkedSource === SOURCE.PLAN_CHANGE &&
        !isChecked &&
        !isOrgAddonCheckedByDefault
      ) {
        skipRemovedUpdate = true;
      }

      if (addOnType === ADD_ON_TYPES.CURRICULUM_LEVEL) {
        const curriculumStatus = _.get(addOnDetails, "curriculumStatus", []);
        const curriculum = _.find(curriculumStatus, { id: entityId });
        const curriculumCheckedSource = _.get(curriculum, "source", null);
        const isCurriculumCheckedByDefault = _.get(
          curriculum,
          "checked",
          false
        );

        if (
          curriculumCheckedSource === SOURCE.PLAN_CHANGE &&
          !isChecked &&
          !isCurriculumCheckedByDefault
        ) {
          skipRemovedUpdate = true;
        }
      }

      const updatedAdded = isChecked
        ? _.uniq([...added, entityId])
        : _.without(added, entityId);

      const updatedRemoved = isChecked
        ? _.without(removed, entityId)
        : _.uniq([...removed, entityId]);

      return {
        ...prevState,
        [addOnId]: {
          ...prevConfig,
          addedEntities: updatedAdded,
          ...(skipRemovedUpdate ? {} : { removedEntities: updatedRemoved }),
        },
      };
    });
  };

  const renderConfigurableComponent = () => {
    const addOn = _.get(selectedAddOn, "addOn", null);
    const addOnId = _.get(addOn, "id", null);
    const curriculumProgramId = _.get(selectedAddOn, "entityId", null);
    const onSuccess = () =>
      onSuccessAddOnConfigChange({ addOnId, curriculumProgramId });

    if (!addOnId) return null;

    switch (addOnId) {
      case WORKRAMP_ACADEMY: {
        return (
          <WorkrampAcademy
            closeModal={closeAddOnModal}
            orgRegion={orgRegion}
            onSuccessAddOnConfigChange={onSuccess}
            orgId={orgId}
            addOn={addOn}
          />
        );
      }
      case AUTH_AND_EVAL: {
        return (
          <ManageAccessForSnP
            closeModal={closeAddOnModal}
            onSuccessAddOnConfigChange={onSuccess}
            curriculumProgramId={curriculumProgramId}
            organizationId={orgId}
            testId={testId}
          />
        );
      }
      case TODDLE_AI: {
        return (
          <ToddleAi
            onSuccessAddOnConfigChange={onSuccess}
            curriculumProgramIds={[curriculumProgramId]}
            closeModal={closeAddOnModal}
            orgId={orgId}
          />
        );
      }
      case AI_TUTOR: {
        return (
          <AITutor
            closeModal={closeAddOnModal}
            orgId={orgId}
            curriculumProgramIds={[curriculumProgramId]}
            onSuccessAddOnConfigChange={onSuccess}
          />
        );
      }
      case UNIT_PLANNING_AI: {
        return <UnitPlanningAI closeModal={closeAddOnModal} orgId={orgId} />;
      }
      case TODDLE_LIBRARY: {
        return (
          <ManageAccessForToddleLibrary
            closeModal={closeAddOnModal}
            onSuccessAddOnConfigChange={onSuccess}
            curriculumProgramId={curriculumProgramId}
            organizationId={orgId}
          />
        );
      }
      case MESSAGING:
        return (
          <ManageMessaging
            onSuccessAddOnConfigChange={onSuccess}
            closeModal={closeAddOnModal}
            orgId={orgId}
          />
        );
      case IBIS_REGISTRATION_REVAMP: {
        return (
          <IBISRevamp
            onSuccessAddOnConfigChange={onSuccess}
            orgId={orgId}
            closeModal={closeAddOnModal}
          />
        );
      }
      case TODDLE_ANALYTICS: {
        return (
          <ToddleAnalytics
            curriculumProgramIds={[curriculumProgramId]}
            onSuccessAddOnConfigChange={onSuccess}
            closeModal={closeAddOnModal}
            orgId={orgId}
          />
        );
      }
      case ROLES_AND_PERMISSIONS: {
        return (
          <RolesAndPermissions
            onSuccessAddOnConfigChange={onSuccess}
            orgId={orgId}
            closeModal={closeAddOnModal}
          />
        );
      }
      case BLUEPRINT_SYNC: {
        return (
          <BluePrintSync
            onSuccessAddOnConfigChange={onSuccess}
            orgId={orgId}
            closeModal={closeAddOnModal}
          />
        );
      }
      case ROSTER_SYNC: {
        return (
          <RosterSync
            orgId={orgId}
            onSuccessAddOnConfigChange={onSuccess}
            closeModal={closeAddOnModal}
          />
        );
      }
      case TIMETABLE_SYNC: {
        return (
          <TimetableSync
            orgId={orgId}
            onSuccessAddOnConfigChange={onSuccess}
            closeModal={closeAddOnModal}
          />
        );
      }
      case ATTENDANCE_SYNC: {
        return (
          <AttendanceSync
            orgId={orgId}
            onSuccessAddOnConfigChange={onSuccess}
            closeModal={closeAddOnModal}
          />
        );
      }
      default:
        return null;
    }
  };

  const getTableHeader = ({ orgLevel }) => {
    const table = orgLevel ? "tableOne" : "tableTwo";

    return (
      <div className={classes.pageHeaderContainer}>
        <h6>{_.get(pageInfo, `${table}Heading`)}</h6>
        <div className={classNames(classes.pageDescription, "text-body")}>
          {_.get(pageInfo, `${table}Info`)}
        </div>
      </div>
    );
  };

  const containerClassList = classNames(classes.container, {
    [classes.toddle20Container]: isToddle20Flow,
  });

  const onSaveAddons = async () => {
    try {
      setIsLoading(LOADING_STATES.SAVING);
      await toggleAddOnsSelection({
        configurationState,
        addOnsStatus,
        orgId,
        orgRegion,
      });
      closeModal();
    } catch (error) {
    } finally {
      setIsLoading(null);
      toggleConfirmationModal();
    }
  };

  const toggleConfirmationModal = () => {
    setShowConfirmationModal(!showConfirmationModal);
  };

  return (
    <div className={containerClassList}>
      {!_.isEmpty(modifiedSchoolPlanDetails) && (
        <Alert
          type="info"
          message="Some add-ons have been updated due to plan changes or the addition of new schools. Please review and verify them."
          style={alertStyles}
        />
      )}
      {showConfirmationModal && (
        <AddonsConfirmationModal
          closeModal={toggleConfirmationModal}
          onSave={onSaveAddons}
          testId={testId}
          curriculumPrograms={curriculumPrograms}
          toggledOrgAddons={toggledOrgAddons}
          toggledCurriculumAddons={toggledCurriculumAddons}
        />
      )}
      <div className={classes.mainComponent}>
        {getTableHeader({ orgLevel: true })}
        <AddonsDatagrid
          t={t}
          isLoading={_.isEqual(isLoading, LOADING_STATES.FETCHING)}
          addOnOptions={organizationAddOns}
          addOnsStatus={addOnsStatus}
          organizationDetails={organizationDetails}
          isAccountDatagrid={true}
          onToggleCheckbox={onToggleCheckbox}
          toggleAddOn={toggleAddOn}
          configurationState={configurationState}
        />
        {getTableHeader({ orgLevel: false })}
        <AddonsDatagrid
          t={t}
          isLoading={_.isEqual(isLoading, LOADING_STATES.FETCHING)}
          addOnOptions={curriculumAddOns}
          addOnsStatus={addOnsStatus}
          organizationDetails={organizationDetails}
          isAccountDatagrid={false}
          onToggleCheckbox={onToggleCheckbox}
          toggleAddOn={toggleAddOn}
          configurationState={configurationState}
        />
      </div>
      {!_.isNull(selectedAddOn) ? renderConfigurableComponent() : null}
      {_.isEqual(isLoading, LOADING_STATES.SAVING) ? (
        <FullScreenLoader />
      ) : null}
    </div>
  );
};

const mapActionCreators = {
  setPortalType,
  toggleAddOnsSelection,
};

export default connect(null, mapActionCreators)(AddOnManager);
